/**
 * SSO API — Microsoft Entra ID sign-in routing, callback, CA admin actions.
 *
 * Mirrors the patterns in ``auth.ts``: Zod-validated responses, discriminated
 * union return types for endpoints that have multiple legal outcomes, and
 * stable error codes the UI maps to user-facing messages.
 */

import { z } from "zod";
import {
  api,
  apiRaw,
  extractCSRFToken,
} from "./client";

// ── Discover (email-first routing) ────────────────────────

const DiscoverResponseSchema = z.object({
  method: z.enum(["password", "sso_entra"]),
  authorize_url: z.string().url().nullable().optional(),
});
export type DiscoverResponse = z.infer<typeof DiscoverResponseSchema>;

interface DiscoverRequest {
  /** Optional. When the user clicks "Sign in with Microsoft" without
   *  typing a work email first, the backend issues a generic SSO
   *  authorize URL (no ``login_hint``) and resolves the company on
   *  callback via the validated ``tid`` claim. When the user did type
   *  an email, we send it so Microsoft can pre-pick the tenant and
   *  account. */
  email?: string;
  /** Set when the user is on the invitation accept page so the backend
   *  can attach the invitation to the SSO state. */
  invitation_token?: string;
  device_fingerprint: string;
  device_name: string;
}

/** Ask the backend whether ``email`` should sign in via password or SSO.
 *  Unknown emails always resolve to ``password`` — the response NEVER
 *  reveals account existence. */
export async function discover(
  request: DiscoverRequest,
): Promise<DiscoverResponse> {
  return api("/auth/sso/discover", DiscoverResponseSchema, {
    method: "POST",
    body: request,
  });
}

// ── Callback (from /auth/callback/microsoft) ──────────────

const SsoCallbackResponseSchema = z.object({
  user_id: z.string().uuid(),
  company_id: z.string().uuid().nullable(),
  status: z.string(),
  is_global_admin: z.boolean(),
  is_company_admin: z.boolean(),
  must_change_password: z.boolean(),
  mfa_enabled: z.boolean(),
  newly_linked: z.boolean(),
});
export type SsoCallbackResponse = z.infer<typeof SsoCallbackResponseSchema>;

const SsoSessionConflictSchema = z.object({
  error: z.literal("session_conflict"),
  message: z.string(),
  active_device: z.string(),
  last_activity: z.string(),
  resume_token: z.string(),
});
export type SsoSessionConflict = z.infer<typeof SsoSessionConflictSchema>;

/** Discriminated union mirroring auth.ts ``LoginResult`` — every legal
 *  callback outcome has a distinct ``type`` so the UI can render the right
 *  surface. */
export type SsoCallbackResult =
  | { type: "success"; data: SsoCallbackResponse }
  | {
      type: "session_conflict";
      active_device: string;
      last_activity: string;
      resume_token: string;
    }
  | { type: "error"; code: string; message: string; retryAfter?: number };

/** Exchange the ``code`` returned in the callback URL for a session.
 *  ``state`` is consumed server-side and validated against the cached
 *  state record from the discover call. */
export async function callback(
  code: string,
  state: string,
): Promise<SsoCallbackResult> {
  return _runCallback("/auth/sso/microsoft/callback", { code, state });
}

/** Resume an SSO sign-in after the user confirmed a session takeover.
 *  Same shape as ``callback``; on success returns the session, on
 *  failure returns the same stable error codes. */
export async function callbackConfirm(
  resumeToken: string,
): Promise<SsoCallbackResult> {
  return _runCallback("/auth/sso/microsoft/callback/confirm", {
    resume_token: resumeToken,
  });
}

async function _runCallback(
  path: string,
  body: Record<string, string>,
): Promise<SsoCallbackResult> {
  const response = await apiRaw(path, { method: "POST", body });
  const json: unknown = await response.json();

  if (response.ok) {
    extractCSRFToken(response);
    const data = SsoCallbackResponseSchema.parse(json);
    return { type: "success", data };
  }

  if (response.status === 409) {
    // Backend wraps the error in ``{detail: {...}}`` for HTTPException —
    // the SSO-specific shape is in ``detail``.
    const detail = (json as { detail?: unknown }).detail ?? json;
    const parsed = SsoSessionConflictSchema.safeParse(detail);
    if (parsed.success) {
      return {
        type: "session_conflict",
        active_device: parsed.data.active_device,
        last_activity: parsed.data.last_activity,
        resume_token: parsed.data.resume_token,
      };
    }
  }

  // Generic error path — extract code+message from either FastAPI's
  // ``{detail: {error, message}}`` envelope or our flat shape.
  const detail = (json as { detail?: unknown }).detail;
  const errorBody = (detail ?? json) as Record<string, unknown>;

  if (response.status === 429) {
    return {
      type: "error",
      code: "rate_limited",
      message:
        (errorBody["message"] as string) ??
        "Too many sign-in attempts. Please try again in a few minutes.",
      ...(typeof errorBody["retry_after"] === "number"
        ? { retryAfter: errorBody["retry_after"] }
        : {}),
    };
  }

  return {
    type: "error",
    code: (errorBody["error"] as string) ?? "sso_unknown_error",
    message: (errorBody["message"] as string) ?? "Microsoft sign-in failed.",
  };
}

// ── Company admin: SSO config ─────────────────────────────

const SsoConfigSchema = z.object({
  sso_enabled: z.boolean(),
  sso_tenant_id: z.string().uuid().nullable(),
  sso_mode: z.enum(["optional", "required_new", "required_all"]),
  sso_block_b2b_guests: z.boolean(),
  sso_connected_at: z.string().nullable(),
  sso_required_amr: z.array(z.string()),
  sso_refresh_token_expire_minutes: z.number().nullable(),
});
export type SsoConfig = z.infer<typeof SsoConfigSchema>;

export async function getSsoConfig(): Promise<SsoConfig> {
  return api("/companies/me/sso/config", SsoConfigSchema);
}

export interface SsoConfigUpdate {
  sso_enabled?: boolean;
  sso_mode?: "optional" | "required_new" | "required_all";
  sso_block_b2b_guests?: boolean;
  sso_required_amr?: string[];
  sso_refresh_token_expire_minutes?: number;
}

export async function updateSsoConfig(
  update: SsoConfigUpdate,
): Promise<SsoConfig> {
  return api("/companies/me/sso/config", SsoConfigSchema, {
    method: "PATCH",
    body: update,
  });
}

// ── Company admin: tenant connect / disconnect ────────────

const SsoConnectInitiateSchema = z.object({
  consent_url: z.string().url(),
  state: z.string(),
});
export type SsoConnectInitiate = z.infer<typeof SsoConnectInitiateSchema>;

/** Generate the admin-consent URL the CA opens in a new tab. The
 *  customer's tenant admin grants consent there; Microsoft redirects
 *  back with ``tenant=<tid>`` which the frontend passes to ``finalizeConnect``. */
export async function initiateConnect(): Promise<SsoConnectInitiate> {
  return api(
    "/companies/me/sso/connect",
    SsoConnectInitiateSchema,
    { method: "POST" },
  );
}

export async function finalizeConnect(
  tenantId: string,
  state: string,
): Promise<SsoConfig> {
  // No auth required — the state token IS the authorization. Critical
  // because the customer's tenant admin (who lands here on Microsoft's
  // redirect after granting consent) typically has NO MI session and
  // is in a DIFFERENT browser from the CA who started the flow.
  return api(
    "/auth/sso/microsoft/connect/finalize",
    SsoConfigSchema,
    {
      method: "POST",
      body: { tenant_id: tenantId, state },
    },
  );
}

/** Disconnect the customer's tenant. By default refuses with 409 if SSO
 *  users still exist; pass ``confirmLockout=true`` to proceed.
 *
 *  Returns:
 *    - SsoConfig on success
 *    - { lockout: { count, sample_emails } } when a confirm is required
 */
export type DisconnectResult =
  | { type: "success"; config: SsoConfig }
  | {
      type: "would_lock_out";
      message: string;
    };

export async function disconnect(
  confirmLockout = false,
): Promise<DisconnectResult> {
  const path = confirmLockout
    ? "/companies/me/sso/connect?confirm_lockout=true"
    : "/companies/me/sso/connect";
  const response = await apiRaw(path, { method: "DELETE" });
  const json: unknown = await response.json();

  if (response.ok) {
    return { type: "success", config: SsoConfigSchema.parse(json) };
  }

  if (response.status === 409) {
    const detail = (json as { detail?: unknown }).detail ?? json;
    const parsed = z
      .object({
        error: z.literal("sso_disconnect_would_lock_users"),
        message: z.string(),
      })
      .safeParse(detail);
    if (parsed.success) {
      return {
        type: "would_lock_out",
        message: parsed.data.message,
      };
    }
  }

  // Unexpected — surface as a thrown error for the calling component to
  // handle generically (toast, etc.).
  const detail = (json as { detail?: unknown }).detail;
  const errorBody = (detail ?? json) as Record<string, unknown>;
  throw new Error(
    (errorBody["message"] as string) ?? "Failed to disconnect Microsoft SSO",
  );
}

// ── Company admin: per-user auth-method conversion ────────

const AuthMethodChangeResponseSchema = z.object({
  user_id: z.string().uuid(),
  method: z.enum(["password", "sso_entra"]),
  pending_invitation_id: z.string().uuid().nullable(),
  message: z.string(),
});
export type AuthMethodChangeResponse = z.infer<
  typeof AuthMethodChangeResponseSchema
>;

/** CA-driven: switch a user from password to SSO. Issues a re-link
 *  invitation; the user clicks the email and completes the SSO flow.
 *  Idempotent — calling twice returns the same pending invitation id. */
export async function convertUserToSso(
  userId: string,
): Promise<AuthMethodChangeResponse> {
  return api(
    `/companies/me/users/${userId}/auth-method`,
    AuthMethodChangeResponseSchema,
    {
      method: "PATCH",
      body: { method: "sso_entra" },
    },
  );
}

/** CA-driven: switch a user from SSO back to password. Clears the
 *  Microsoft linkage, locks the account with an unguessable random
 *  password, kills sessions, sends a password-reset email. */
export async function revertUserToPassword(
  userId: string,
): Promise<AuthMethodChangeResponse> {
  return api(
    `/companies/me/users/${userId}/auth-method`,
    AuthMethodChangeResponseSchema,
    {
      method: "PATCH",
      body: { method: "password" },
    },
  );
}
